// AI Employees data for frontend use
// This is a simplified version of the worker data

export interface AIEmployee {
  id: string;
  name: string;
  role: string;
  description: string;
  avatar: string; // Emoji avatar
  color: string;
  gradient: string;
  category: string;
  capabilities: string[];
  isNew?: boolean;
  isComingSoon?: boolean;
}

export const AI_EMPLOYEES: AIEmployee[] = [
  {
    id: 'agent',
    name: 'Agent',
    role: 'Conversational Agent',
    description: 'Your universal AI assistant and expert on all subjects. Ask anything and get intelligent answers, or get connected to the right specialist for your needs.',
    avatar: '🤖',
    color: '#0ea5e9',
    gradient: 'from-sky-500 to-blue-500',
    category: 'general',
    capabilities: [
      'General knowledge on any topic',
      'Intelligent question answering',
      'Smart routing to specialists',
      'Research and information gathering',
      'Problem solving and advice',
      'Multi-domain expertise',
    ],
  },
  {
    id: 'aria',
    name: 'Aria',
    role: 'Social Media Manager',
    description: 'Expert at creating viral content, managing social presence, and building engaged communities across all platforms.',
    avatar: '🎀',
    color: '#ec4899',
    gradient: 'from-pink-500 to-rose-500',
    category: 'marketing',
    capabilities: [
      'Content calendar planning',
      'Post creation and scheduling',
      'Hashtag research and optimization',
      'Engagement analytics',
      'Trend monitoring',
      'Community management',
    ],
  },
  {
    id: 'nova',
    name: 'Nova',
    role: 'Customer Support',
    description: 'Delivers exceptional customer experiences with empathy, efficiency, and expertise in resolving any inquiry.',
    avatar: '💙',
    color: '#06b6d4',
    gradient: 'from-cyan-500 to-teal-500',
    category: 'support',
    capabilities: [
      'Multi-channel support',
      'Ticket management',
      'FAQ creation',
      'Response templates',
      'Escalation handling',
      'Customer sentiment analysis',
    ],
  },
  {
    id: 'atlas',
    name: 'Atlas',
    role: 'Data Analyst & Algorithm Expert',
    description: 'Expert in algorithms, data analysis, and computational problem-solving. Writes textbook-quality algorithms, analyzes data files, reads algorithm questions from images, and makes data-driven decisions.',
    avatar: '📊',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-purple-500',
    category: 'operations',
    capabilities: [
      'Algorithm design and writing',
      'Data analysis and interpretation',
      'Image analysis for algorithms',
      'Data file processing (CSV, JSON)',
      'Statistical analysis',
      'Decision making from data',
    ],
  },
  {
    id: 'sage',
    name: 'Sage',
    role: 'Business Strategist',
    description: 'Provides expert strategic guidance to drive growth, optimize operations, and outmaneuver competition.',
    avatar: '🧠',
    color: '#10b981',
    gradient: 'from-emerald-500 to-green-500',
    category: 'operations',
    capabilities: [
      'Business strategy development',
      'Market analysis',
      'Growth planning',
      'Competitive intelligence',
      'SWOT analysis',
      'Business model optimization',
    ],
  },
  {
    id: 'echo',
    name: 'Echo',
    role: 'Email Marketing',
    description: 'Crafts high-converting email campaigns that nurture leads and drive consistent revenue growth.',
    avatar: '📧',
    color: '#f97316',
    gradient: 'from-orange-500 to-amber-500',
    category: 'marketing',
    capabilities: [
      'Email campaign creation',
      'Newsletter design',
      'Automation sequences',
      'A/B testing strategies',
      'Subject line optimization',
      'List segmentation',
    ],
  },
  {
    id: 'pulse',
    name: 'Pulse',
    role: 'Personal Growth',
    description: 'Your dedicated coach for wellness, productivity, and personal development goals.',
    avatar: '💪',
    color: '#ef4444',
    gradient: 'from-red-500 to-rose-500',
    category: 'personal',
    capabilities: [
      'Goal setting and tracking',
      'Productivity systems',
      'Habit building',
      'Time management',
      'Wellness planning',
      'Workout routines',
    ],
  },
  {
    id: 'scout',
    name: 'Scout',
    role: 'Recruiter',
    description: 'Expert at finding, screening, and onboarding top talent that fits your culture.',
    avatar: '🔍',
    color: '#14b8a6',
    gradient: 'from-teal-500 to-cyan-500',
    category: 'operations',
    capabilities: [
      'Job description writing',
      'Candidate sourcing',
      'Resume screening',
      'Interview questions',
      'Onboarding plans',
      'Employer branding',
    ],
  },
  {
    id: 'ink',
    name: 'Ink',
    role: 'Copywriter',
    description: 'Creates compelling copy that converts readers into customers with powerful words.',
    avatar: '✍️',
    color: '#6366f1',
    gradient: 'from-indigo-500 to-violet-500',
    category: 'creative',
    capabilities: [
      'Ad copy creation',
      'Landing page copy',
      'Blog writing',
      'Product descriptions',
      'Brand messaging',
      'Headlines and taglines',
    ],
  },
  {
    id: 'flux',
    name: 'Flux',
    role: 'eCommerce Manager',
    description: 'Optimizes your online store for maximum conversions and customer satisfaction.',
    avatar: '🛒',
    color: '#f59e0b',
    gradient: 'from-amber-500 to-yellow-500',
    category: 'sales',
    capabilities: [
      'Product listing optimization',
      'Pricing strategy',
      'Inventory management',
      'Conversion optimization',
      'Customer journey mapping',
      'Upsell strategies',
    ],
  },
  {
    id: 'spark',
    name: 'Spark',
    role: 'Sales Specialist',
    description: 'Closes deals with persuasive outreach, compelling pitches, and strategic follow-ups.',
    avatar: '🎯',
    color: '#22c55e',
    gradient: 'from-green-500 to-emerald-500',
    category: 'sales',
    capabilities: [
      'Cold email sequences',
      'Sales pitch creation',
      'Objection handling',
      'Follow-up strategies',
      'Proposal writing',
      'CRM optimization',
    ],
  },
  {
    id: 'orbit',
    name: 'Orbit',
    role: 'SEO Expert',
    description: 'Dominates search rankings with strategic optimization and content that drives organic traffic.',
    avatar: '🚀',
    color: '#3b82f6',
    gradient: 'from-blue-500 to-indigo-500',
    category: 'marketing',
    capabilities: [
      'Keyword research',
      'On-page optimization',
      'Content strategy',
      'Technical SEO audits',
      'Backlink strategies',
      'Local SEO',
    ],
  },
  {
    id: 'sync',
    name: 'Sync',
    role: 'Virtual Assistant',
    description: 'Your reliable assistant for scheduling, organization, and daily task management.',
    avatar: '📅',
    color: '#a855f7',
    gradient: 'from-purple-500 to-fuchsia-500',
    category: 'personal',
    capabilities: [
      'Calendar management',
      'Meeting scheduling',
      'Task prioritization',
      'Travel planning',
      'Expense tracking',
      'Research tasks',
    ],
  },
  {
    id: 'code',
    name: 'Code',
    role: 'Developer Assistant',
    description: 'Your coding companion for debugging, documentation, and development best practices.',
    avatar: '💻',
    color: '#64748b',
    gradient: 'from-slate-500 to-gray-500',
    category: 'technical',
    capabilities: [
      'Code review',
      'Bug debugging',
      'Documentation writing',
      'API design',
      'Performance optimization',
      'Security audits',
    ],
  },
  {
    id: 'pixel',
    name: 'Pixel',
    role: 'Design Assistant',
    description: 'Provides expert design feedback and maintains your visual brand consistency.',
    avatar: '🎨',
    color: '#f472b6',
    gradient: 'from-pink-400 to-rose-400',
    category: 'creative',
    capabilities: [
      'Design feedback',
      'Brand guidelines',
      'UI/UX suggestions',
      'Color palette creation',
      'Typography selection',
      'Visual hierarchy',
    ],
  },
  {
    id: 'legal',
    name: 'Legal',
    role: 'Compliance Assistant',
    description: 'Helps navigate legal requirements with policy drafting and compliance guidance.',
    avatar: '⚖️',
    color: '#78716c',
    gradient: 'from-stone-500 to-neutral-500',
    category: 'operations',
    capabilities: [
      'Policy drafting',
      'Terms of service',
      'Privacy policy',
      'Contract review',
      'GDPR compliance',
      'Risk assessment',
    ],
  },
  {
    id: 'mvp',
    name: 'MVP Maker',
    role: 'Prototype Builder',
    description: 'Transforms your ideas into fully functional prototypes. From concept to production-ready MVP in one conversation.',
    avatar: '🚀',
    color: '#8b5cf6',
    gradient: 'from-violet-500 to-indigo-500',
    category: 'technical',
    isComingSoon: true,
    capabilities: [
      'Full-stack prototyping',
      'Architecture design',
      'Database schema',
      'API development',
      'Frontend UI/UX',
      'Deployment setup',
    ],
  },
  {
    id: 'slides',
    name: 'Slides',
    role: 'Presentation Maker',
    description: 'Creates stunning professional presentations with animations, transitions, and beautiful typography. Like Gamma AI, Tome, and Canva.',
    avatar: '📊',
    color: '#f97316',
    gradient: 'from-orange-500 to-red-500',
    category: 'creative',
    capabilities: [
      'Professional slide design',
      'Animated presentations',
      'Beautiful typography',
      'Slide transitions & effects',
      'Content structure & flow',
      'Visual storytelling',
      'Data visualization slides',
      'Pitch deck creation',
    ],
  },
];

// Helper functions
export function getEmployeeById(id: string) {
  return AI_EMPLOYEES.find((e) => e.id === id);
}

export function getEmployeesByCategory(category: string) {
  return AI_EMPLOYEES.filter((e) => e.category === category);
}

export const EMPLOYEE_CATEGORIES = [
  { id: 'general', name: 'General', icon: 'bot' },
  { id: 'marketing', name: 'Marketing', icon: 'megaphone' },
  { id: 'sales', name: 'Sales', icon: 'trending-up' },
  { id: 'support', name: 'Support', icon: 'headphones' },
  { id: 'operations', name: 'Operations', icon: 'settings' },
  { id: 'creative', name: 'Creative', icon: 'palette' },
  { id: 'technical', name: 'Technical', icon: 'code' },
  { id: 'personal', name: 'Personal', icon: 'user' },
];

// Extended capability descriptions for the "See More" modal
// Maps capability titles to longer titles and detailed descriptions
export const CAPABILITY_DETAILS: Record<string, { title: string; description: string; prompt: string }> = {
  // Agent capabilities
  'General knowledge on any topic': {
    title: 'Answer Questions on Any Topic',
    description: 'Get comprehensive answers about any subject - from science and history to business and technology',
    prompt: 'I have a question about a topic I need help understanding',
  },
  'Intelligent question answering': {
    title: 'Get Intelligent, In-Depth Answers',
    description: 'Receive detailed, well-researched responses with context, examples, and actionable insights',
    prompt: 'I need a detailed explanation with examples and insights',
  },
  'Smart routing to specialists': {
    title: 'Connect Me to the Right Expert',
    description: 'Get automatically matched with the best AI specialist for your specific task or question',
    prompt: 'Help me find the right specialist for my task',
  },
  'Research and information gathering': {
    title: 'Research and Gather Information',
    description: 'Conduct thorough research on any topic with summarized findings and key takeaways',
    prompt: 'I need help researching a topic thoroughly',
  },
  'Problem solving and advice': {
    title: 'Help Me Solve a Problem',
    description: 'Get strategic advice and step-by-step solutions for challenges you\'re facing',
    prompt: 'I need help solving a problem and want strategic advice',
  },
  'Multi-domain expertise': {
    title: 'Multi-Domain Expert Consultation',
    description: 'Access combined expertise across multiple fields for complex, interdisciplinary questions',
    prompt: 'I have a complex question that spans multiple areas of expertise',
  },

  // Aria (Social Media) capabilities
  'Content calendar planning': {
    title: 'Plan My Content Calendar',
    description: 'Create a strategic weekly or monthly content schedule optimized for engagement and consistency',
    prompt: 'Help me create a content calendar for my social media strategy',
  },
  'Post creation and scheduling': {
    title: 'Create and Schedule Social Posts',
    description: 'Generate engaging posts tailored for each platform with optimal posting times',
    prompt: 'Help me create engaging social media posts for my brand',
  },
  'Hashtag research and optimization': {
    title: 'Find the Best Hashtags',
    description: 'Discover trending and niche-specific hashtags to maximize your content\'s reach and visibility',
    prompt: 'Help me find the best hashtags for my content niche',
  },
  'Engagement analytics': {
    title: 'Analyze My Engagement Metrics',
    description: 'Understand your social media performance with insights on what\'s working and what to improve',
    prompt: 'Help me analyze my social media engagement and suggest improvements',
  },
  'Trend monitoring': {
    title: 'Track Social Media Trends',
    description: 'Stay ahead of viral trends and topics relevant to your industry and audience',
    prompt: 'Help me identify trending topics I should create content about',
  },
  'Community management': {
    title: 'Manage My Online Community',
    description: 'Build and nurture engaged communities with response strategies and engagement tactics',
    prompt: 'Help me build and manage an engaged community around my brand',
  },
  'Influencer outreach': {
    title: 'Plan Influencer Outreach Strategy',
    description: 'Identify and connect with influencers who align with your brand for partnerships',
    prompt: 'Help me create an influencer outreach strategy',
  },
  'Platform-specific strategies': {
    title: 'Create Platform-Specific Strategies',
    description: 'Get tailored strategies for Instagram, TikTok, LinkedIn, Twitter, and other platforms',
    prompt: 'Help me create a strategy specific to my target platform',
  },

  // Nova (Customer Support) capabilities
  'Multi-channel support': {
    title: 'Set Up Multi-Channel Support',
    description: 'Create consistent support experiences across email, chat, social media, and phone',
    prompt: 'Help me set up consistent support across all my customer channels',
  },
  'Ticket management': {
    title: 'Organize My Support Tickets',
    description: 'Implement efficient ticket routing, prioritization, and resolution workflows',
    prompt: 'Help me create an efficient ticket management system',
  },
  'FAQ creation': {
    title: 'Build a Comprehensive FAQ',
    description: 'Create detailed FAQs that answer common questions and reduce support volume',
    prompt: 'Help me create a comprehensive FAQ for my product or service',
  },
  'Response templates': {
    title: 'Create Support Response Templates',
    description: 'Build professional, empathetic templates for common support scenarios',
    prompt: 'Help me create response templates for common customer inquiries',
  },
  'Escalation handling': {
    title: 'Design Escalation Procedures',
    description: 'Set up clear escalation paths for complex issues and VIP customers',
    prompt: 'Help me design escalation procedures for difficult customer issues',
  },
  'Customer sentiment analysis': {
    title: 'Analyze Customer Sentiment',
    description: 'Understand how customers feel about your product through feedback analysis',
    prompt: 'Help me analyze customer feedback and identify sentiment patterns',
  },
  'Knowledge base management': {
    title: 'Build a Knowledge Base',
    description: 'Create and organize self-service documentation for customers and support teams',
    prompt: 'Help me create an organized knowledge base for my customers',
  },
  'SLA monitoring': {
    title: 'Set Up SLA Monitoring',
    description: 'Define and track service level agreements to ensure timely support responses',
    prompt: 'Help me set up SLA tracking and monitoring for my support team',
  },

  // Atlas (Data Analyst) capabilities
  'Algorithm design and writing': {
    title: 'Design and Write Algorithms',
    description: 'Get textbook-quality algorithms with pseudocode, complexity analysis, and examples',
    prompt: 'Help me design an algorithm with proper documentation and analysis',
  },
  'Data analysis and interpretation': {
    title: 'Analyze and Interpret My Data',
    description: 'Extract meaningful insights from your data with clear visualizations and recommendations',
    prompt: 'Help me analyze my data and extract actionable insights',
  },
  'Image analysis for algorithms': {
    title: 'Solve Algorithm Problems from Images',
    description: 'Upload screenshots of algorithm problems and get complete solutions',
    prompt: 'Help me solve this algorithm problem (upload an image)',
  },
  'Data file processing (CSV, JSON)': {
    title: 'Process and Transform Data Files',
    description: 'Clean, transform, and analyze CSV, JSON, and other data formats',
    prompt: 'Help me process and analyze my data files',
  },
  'Statistical analysis': {
    title: 'Perform Statistical Analysis',
    description: 'Apply statistical methods to understand patterns, correlations, and significance in your data',
    prompt: 'Help me perform statistical analysis on my dataset',
  },
  'Decision making from data': {
    title: 'Make Data-Driven Decisions',
    description: 'Get recommendations backed by data analysis to inform your business decisions',
    prompt: 'Help me make a decision based on this data',
  },
  'Complexity analysis (Big O)': {
    title: 'Analyze Time & Space Complexity',
    description: 'Understand the efficiency of algorithms with Big O notation analysis',
    prompt: 'Help me analyze the time and space complexity of this algorithm',
  },
  'Report generation': {
    title: 'Generate Data Reports',
    description: 'Create professional reports with insights, charts, and executive summaries',
    prompt: 'Help me create a comprehensive data report',
  },
  'Trend forecasting': {
    title: 'Forecast Future Trends',
    description: 'Predict future patterns based on historical data and statistical modeling',
    prompt: 'Help me forecast trends based on my historical data',
  },
  'KPI tracking': {
    title: 'Set Up KPI Tracking',
    description: 'Define and monitor key performance indicators to measure business success',
    prompt: 'Help me set up KPI tracking for my business',
  },

  // Sage (Business Strategist) capabilities
  'Business strategy development': {
    title: 'Develop a Business Strategy',
    description: 'Create comprehensive strategic plans aligned with your goals and market position',
    prompt: 'Help me develop a strategic plan for my business',
  },
  'Market analysis': {
    title: 'Analyze My Market Opportunity',
    description: 'Understand market size, trends, and opportunities for growth in your industry',
    prompt: 'Help me analyze the market opportunity for my business',
  },
  'Growth planning': {
    title: 'Create a Growth Strategy',
    description: 'Develop actionable plans to scale your business sustainably',
    prompt: 'Help me create a growth strategy for scaling my business',
  },
  'Competitive intelligence': {
    title: 'Research My Competition',
    description: 'Analyze competitors\' strengths, weaknesses, and strategies to find your edge',
    prompt: 'Help me analyze my competitors and find competitive advantages',
  },
  'SWOT analysis': {
    title: 'Conduct a SWOT Analysis',
    description: 'Identify your Strengths, Weaknesses, Opportunities, and Threats systematically',
    prompt: 'Help me conduct a SWOT analysis for my business',
  },
  'Business model optimization': {
    title: 'Optimize My Business Model',
    description: 'Refine your revenue streams, cost structure, and value proposition',
    prompt: 'Help me optimize my business model for better profitability',
  },
  'Risk assessment': {
    title: 'Assess Business Risks',
    description: 'Identify potential risks and develop mitigation strategies',
    prompt: 'Help me assess and mitigate risks in my business',
  },
  'Strategic roadmapping': {
    title: 'Build a Strategic Roadmap',
    description: 'Create a timeline of strategic initiatives with milestones and success metrics',
    prompt: 'Help me create a strategic roadmap with clear milestones',
  },

  // Echo (Email Marketing) capabilities
  'Email campaign creation': {
    title: 'Create an Email Campaign',
    description: 'Design high-converting email campaigns with compelling copy and clear CTAs',
    prompt: 'Help me create an email marketing campaign',
  },
  'Newsletter design': {
    title: 'Design My Newsletter',
    description: 'Create engaging newsletters that keep subscribers informed and engaged',
    prompt: 'Help me design an engaging newsletter for my audience',
  },
  'Automation sequences': {
    title: 'Build Email Automation Sequences',
    description: 'Create automated drip campaigns for onboarding, nurturing, and re-engagement',
    prompt: 'Help me create an automated email sequence',
  },
  'A/B testing strategies': {
    title: 'Plan Email A/B Tests',
    description: 'Design experiments to optimize subject lines, content, and send times',
    prompt: 'Help me plan A/B tests for my email campaigns',
  },
  'Subject line optimization': {
    title: 'Optimize My Subject Lines',
    description: 'Craft attention-grabbing subject lines that improve open rates',
    prompt: 'Help me write better subject lines for higher open rates',
  },
  'List segmentation': {
    title: 'Segment My Email List',
    description: 'Organize subscribers into targeted segments for personalized messaging',
    prompt: 'Help me segment my email list for better targeting',
  },
  'Deliverability optimization': {
    title: 'Improve Email Deliverability',
    description: 'Ensure your emails reach inboxes and avoid spam filters',
    prompt: 'Help me improve my email deliverability rates',
  },
  'Win-back campaigns': {
    title: 'Create Win-Back Campaigns',
    description: 'Re-engage inactive subscribers with targeted win-back sequences',
    prompt: 'Help me create a win-back campaign for inactive subscribers',
  },

  // Pulse (Personal Growth) capabilities
  'Goal setting and tracking': {
    title: 'Set and Track My Goals',
    description: 'Define SMART goals and create systems to track your progress effectively',
    prompt: 'Help me set meaningful goals and create a tracking system',
  },
  'Productivity systems': {
    title: 'Build My Productivity System',
    description: 'Implement proven productivity frameworks tailored to your work style',
    prompt: 'Help me build a productivity system that works for me',
  },
  'Habit building': {
    title: 'Build Better Habits',
    description: 'Create sustainable habits using science-backed techniques',
    prompt: 'Help me build positive habits that stick',
  },
  'Time management': {
    title: 'Improve My Time Management',
    description: 'Master your schedule with effective time blocking and prioritization',
    prompt: 'Help me manage my time more effectively',
  },
  'Wellness planning': {
    title: 'Create a Wellness Plan',
    description: 'Develop a holistic plan for physical, mental, and emotional well-being',
    prompt: 'Help me create a comprehensive wellness plan',
  },
  'Workout routines': {
    title: 'Design My Workout Routine',
    description: 'Get personalized exercise plans based on your goals and available equipment',
    prompt: 'Help me create a workout routine for my fitness goals',
  },
  'Meal planning': {
    title: 'Plan My Meals',
    description: 'Create balanced meal plans aligned with your nutritional goals',
    prompt: 'Help me create a healthy meal plan',
  },
  'Mindfulness practices': {
    title: 'Start Mindfulness Practices',
    description: 'Learn meditation and mindfulness techniques for stress reduction and focus',
    prompt: 'Help me start a mindfulness practice',
  },

  // Scout (Recruiter) capabilities
  'Job description writing': {
    title: 'Write a Job Description',
    description: 'Create compelling job postings that attract qualified candidates',
    prompt: 'Help me write an effective job description',
  },
  'Candidate sourcing': {
    title: 'Source Quality Candidates',
    description: 'Develop strategies to find and attract top talent for your roles',
    prompt: 'Help me develop a candidate sourcing strategy',
  },
  'Resume screening': {
    title: 'Screen Resumes Effectively',
    description: 'Create criteria and processes to efficiently evaluate applications',
    prompt: 'Help me create a resume screening process',
  },
  'Interview questions': {
    title: 'Prepare Interview Questions',
    description: 'Design role-specific questions that reveal candidate capabilities',
    prompt: 'Help me prepare interview questions for a role',
  },
  'Onboarding plans': {
    title: 'Create an Onboarding Plan',
    description: 'Build comprehensive onboarding programs for new hire success',
    prompt: 'Help me create an onboarding plan for new employees',
  },
  'Employer branding': {
    title: 'Build Employer Brand',
    description: 'Develop your company\'s reputation as an attractive place to work',
    prompt: 'Help me improve my employer branding',
  },
  'Salary benchmarking': {
    title: 'Benchmark Salaries',
    description: 'Research competitive compensation for roles in your industry',
    prompt: 'Help me benchmark salaries for my open positions',
  },
  'Offer letter drafting': {
    title: 'Draft an Offer Letter',
    description: 'Create professional offer letters that close candidates',
    prompt: 'Help me draft a compelling job offer letter',
  },

  // Ink (Copywriter) capabilities
  'Ad copy creation': {
    title: 'Write High-Converting Ad Copy',
    description: 'Create compelling ads for Facebook, Google, Instagram, and other platforms',
    prompt: 'Help me write ad copy that converts',
  },
  'Landing page copy': {
    title: 'Write Landing Page Copy',
    description: 'Craft persuasive landing page content that drives conversions',
    prompt: 'Help me write copy for my landing page',
  },
  'Blog writing': {
    title: 'Write Engaging Blog Posts',
    description: 'Create informative, SEO-friendly blog content for your audience',
    prompt: 'Help me write a blog post for my website',
  },
  'Product descriptions': {
    title: 'Write Product Descriptions',
    description: 'Create compelling product copy that highlights benefits and drives sales',
    prompt: 'Help me write descriptions for my products',
  },
  'Brand messaging': {
    title: 'Develop Brand Messaging',
    description: 'Craft your brand voice, taglines, and core messaging framework',
    prompt: 'Help me develop my brand messaging and voice',
  },
  'Headlines and taglines': {
    title: 'Create Headlines and Taglines',
    description: 'Write attention-grabbing headlines and memorable taglines',
    prompt: 'Help me create compelling headlines and taglines',
  },
  'Sales letters': {
    title: 'Write Sales Letters',
    description: 'Create persuasive long-form sales copy that converts readers to buyers',
    prompt: 'Help me write a sales letter for my product',
  },
  'Website copy': {
    title: 'Write Website Copy',
    description: 'Create clear, engaging copy for all pages of your website',
    prompt: 'Help me write copy for my website pages',
  },

  // Flux (eCommerce) capabilities
  'Product listing optimization': {
    title: 'Optimize Product Listings',
    description: 'Improve product titles, descriptions, and images for better visibility and sales',
    prompt: 'Help me optimize my product listings',
  },
  'Pricing strategy': {
    title: 'Develop Pricing Strategy',
    description: 'Set optimal prices based on costs, competition, and perceived value',
    prompt: 'Help me develop a pricing strategy for my products',
  },
  'Inventory management': {
    title: 'Manage Inventory Effectively',
    description: 'Optimize stock levels to avoid stockouts and reduce carrying costs',
    prompt: 'Help me improve my inventory management',
  },
  'Conversion optimization': {
    title: 'Improve Store Conversions',
    description: 'Identify and fix friction points in your customer journey',
    prompt: 'Help me improve conversion rates in my store',
  },
  'Customer journey mapping': {
    title: 'Map the Customer Journey',
    description: 'Understand and optimize every touchpoint from discovery to purchase',
    prompt: 'Help me map and improve my customer journey',
  },
  'Upsell strategies': {
    title: 'Create Upsell Strategies',
    description: 'Increase average order value with effective upsell and cross-sell tactics',
    prompt: 'Help me create upsell and cross-sell strategies',
  },
  'Review management': {
    title: 'Manage Customer Reviews',
    description: 'Build a system to collect, respond to, and leverage customer reviews',
    prompt: 'Help me manage and leverage customer reviews',
  },
  'Store analytics': {
    title: 'Analyze Store Performance',
    description: 'Understand your store metrics and identify opportunities for improvement',
    prompt: 'Help me analyze my eCommerce store performance',
  },

  // Spark (Sales) capabilities
  'Cold email sequences': {
    title: 'Create Cold Email Sequences',
    description: 'Build multi-touch outreach sequences that get responses from prospects',
    prompt: 'Help me create a cold email outreach sequence',
  },
  'Sales pitch creation': {
    title: 'Craft My Sales Pitch',
    description: 'Develop compelling pitches tailored to different buyer personas',
    prompt: 'Help me create an effective sales pitch',
  },
  'Objection handling': {
    title: 'Handle Sales Objections',
    description: 'Prepare responses for common objections that turn hesitation into yes',
    prompt: 'Help me prepare for common sales objections',
  },
  'Follow-up strategies': {
    title: 'Build Follow-Up Strategies',
    description: 'Create systematic follow-up sequences that close more deals',
    prompt: 'Help me create an effective follow-up strategy',
  },
  'Proposal writing': {
    title: 'Write Winning Proposals',
    description: 'Create professional proposals that clearly communicate value and close deals',
    prompt: 'Help me write a compelling business proposal',
  },
  'CRM optimization': {
    title: 'Optimize My CRM Usage',
    description: 'Set up workflows and automations to maximize your CRM effectiveness',
    prompt: 'Help me optimize my CRM for better sales efficiency',
  },
  'Lead qualification': {
    title: 'Qualify Leads Effectively',
    description: 'Create frameworks to identify and prioritize your best opportunities',
    prompt: 'Help me create a lead qualification framework',
  },
  'Sales scripts': {
    title: 'Write Sales Scripts',
    description: 'Develop call scripts and talking points for consistent sales conversations',
    prompt: 'Help me write effective sales scripts',
  },

  // Orbit (SEO) capabilities
  'Keyword research': {
    title: 'Research Target Keywords',
    description: 'Find high-value keywords with good search volume and achievable competition',
    prompt: 'Help me find the best keywords for my website',
  },
  'On-page optimization': {
    title: 'Optimize On-Page SEO',
    description: 'Improve title tags, meta descriptions, headers, and content for search rankings',
    prompt: 'Help me optimize my pages for search engines',
  },
  'Content strategy': {
    title: 'Build an SEO Content Strategy',
    description: 'Plan content that ranks, drives traffic, and converts visitors',
    prompt: 'Help me create an SEO-focused content strategy',
  },
  'Technical SEO audits': {
    title: 'Audit Technical SEO',
    description: 'Identify and fix technical issues affecting your search performance',
    prompt: 'Help me audit my website for technical SEO issues',
  },
  'Backlink strategies': {
    title: 'Build Quality Backlinks',
    description: 'Develop strategies to earn authoritative links that boost rankings',
    prompt: 'Help me create a backlink building strategy',
  },
  'Local SEO': {
    title: 'Improve Local SEO',
    description: 'Optimize your presence for local searches and Google Business Profile',
    prompt: 'Help me improve my local search visibility',
  },
  'SERP analysis': {
    title: 'Analyze Search Results',
    description: 'Understand what\'s ranking and why to inform your SEO strategy',
    prompt: 'Help me analyze the search results for my target keywords',
  },
  'SEO reporting': {
    title: 'Create SEO Reports',
    description: 'Track and report on key SEO metrics and progress toward goals',
    prompt: 'Help me create an SEO performance report',
  },

  // Sync (Virtual Assistant) capabilities
  'Calendar management': {
    title: 'Organize My Calendar',
    description: 'Optimize your schedule with smart time blocking and buffer time',
    prompt: 'Help me organize and optimize my calendar',
  },
  'Meeting scheduling': {
    title: 'Schedule Meetings Efficiently',
    description: 'Plan and coordinate meetings that respect everyone\'s time',
    prompt: 'Help me schedule a meeting efficiently',
  },
  'Task prioritization': {
    title: 'Prioritize My Tasks',
    description: 'Use proven frameworks to focus on what matters most',
    prompt: 'Help me prioritize my tasks for maximum impact',
  },
  'Travel planning': {
    title: 'Plan My Travel',
    description: 'Create detailed itineraries with flights, hotels, and activities',
    prompt: 'Help me plan my upcoming trip',
  },
  'Expense tracking': {
    title: 'Track My Expenses',
    description: 'Set up systems to monitor and categorize your spending',
    prompt: 'Help me set up expense tracking',
  },
  'Research tasks': {
    title: 'Research a Topic for Me',
    description: 'Get thorough research with summarized findings and sources',
    prompt: 'Help me research a topic and summarize the findings',
  },
  'Document organization': {
    title: 'Organize My Documents',
    description: 'Create filing systems that make finding documents easy',
    prompt: 'Help me organize my documents and files',
  },
  'Reminder systems': {
    title: 'Set Up Reminder Systems',
    description: 'Never miss important dates or tasks with smart reminder setups',
    prompt: 'Help me set up a reminder system',
  },

  // Code (Developer) capabilities
  'Code review': {
    title: 'Review My Code',
    description: 'Get detailed feedback on code quality, patterns, and potential improvements',
    prompt: 'Help me review this code and suggest improvements',
  },
  'Bug debugging': {
    title: 'Debug My Code',
    description: 'Identify and fix bugs with systematic debugging approaches',
    prompt: 'Help me debug this issue in my code',
  },
  'Documentation writing': {
    title: 'Write Technical Documentation',
    description: 'Create clear, comprehensive documentation for your codebase',
    prompt: 'Help me write documentation for my code',
  },
  'API design': {
    title: 'Design My API',
    description: 'Create well-structured, RESTful APIs with proper conventions',
    prompt: 'Help me design a clean API architecture',
  },
  'Performance optimization': {
    title: 'Optimize Performance',
    description: 'Identify bottlenecks and improve your application\'s speed',
    prompt: 'Help me optimize my code for better performance',
  },
  'Security audits': {
    title: 'Audit Security',
    description: 'Review code for vulnerabilities and security best practices',
    prompt: 'Help me audit my code for security issues',
  },
  'Testing strategies': {
    title: 'Plan Testing Strategy',
    description: 'Design comprehensive testing approaches for reliable code',
    prompt: 'Help me create a testing strategy for my project',
  },
  'Architecture advice': {
    title: 'Get Architecture Advice',
    description: 'Plan scalable, maintainable application architectures',
    prompt: 'Help me design the architecture for my application',
  },

  // Pixel (Design) capabilities
  'Design feedback': {
    title: 'Get Design Feedback',
    description: 'Receive professional critique and suggestions to improve your designs',
    prompt: 'Help me get feedback on my design',
  },
  'Brand guidelines': {
    title: 'Create Brand Guidelines',
    description: 'Develop comprehensive brand standards for consistent visual identity',
    prompt: 'Help me create brand guidelines',
  },
  'UI/UX suggestions': {
    title: 'Improve UI/UX Design',
    description: 'Get actionable suggestions to enhance user experience and interface design',
    prompt: 'Help me improve the UI/UX of my product',
  },
  'Color palette creation': {
    title: 'Create a Color Palette',
    description: 'Design harmonious color schemes that reflect your brand personality',
    prompt: 'Help me create a color palette for my brand',
  },
  'Typography selection': {
    title: 'Select Typography',
    description: 'Choose font combinations that enhance readability and brand identity',
    prompt: 'Help me select the right typography for my project',
  },
  'Visual hierarchy': {
    title: 'Improve Visual Hierarchy',
    description: 'Organize design elements to guide users through content effectively',
    prompt: 'Help me improve the visual hierarchy of my design',
  },
  'Design system planning': {
    title: 'Plan a Design System',
    description: 'Create reusable components and standards for consistent designs',
    prompt: 'Help me plan a design system for my product',
  },
  'Accessibility review': {
    title: 'Review Accessibility',
    description: 'Ensure your designs are usable by people with disabilities',
    prompt: 'Help me review my design for accessibility',
  },

  // Legal capabilities
  'Policy drafting': {
    title: 'Draft Legal Policies',
    description: 'Create foundational policies for your business operations',
    prompt: 'Help me draft a policy document',
  },
  'Terms of service': {
    title: 'Write Terms of Service',
    description: 'Create comprehensive terms that protect your business and users',
    prompt: 'Help me write terms of service for my platform',
  },
  'Privacy policy': {
    title: 'Create Privacy Policy',
    description: 'Draft compliant privacy policies that clearly explain data practices',
    prompt: 'Help me create a privacy policy',
  },
  'Contract review': {
    title: 'Review a Contract',
    description: 'Get insights on contract terms, risks, and suggested modifications',
    prompt: 'Help me review this contract and identify concerns',
  },
  'GDPR compliance': {
    title: 'Ensure GDPR Compliance',
    description: 'Understand and implement GDPR requirements for your business',
    prompt: 'Help me ensure my business is GDPR compliant',
  },
  'Legal research': {
    title: 'Research Legal Topics',
    description: 'Get information on legal requirements relevant to your business',
    prompt: 'Help me research legal requirements for my situation',
  },
  'Compliance checklists': {
    title: 'Create Compliance Checklists',
    description: 'Build checklists to ensure you meet regulatory requirements',
    prompt: 'Help me create a compliance checklist',
  },

  // MVP Maker capabilities
  'Full-stack prototyping': {
    title: 'Build a Full-Stack Prototype',
    description: 'Create complete working prototypes with frontend, backend, and database',
    prompt: 'Help me build a full-stack prototype of my idea',
  },
  'Architecture design': {
    title: 'Design System Architecture',
    description: 'Plan scalable architectures for your application',
    prompt: 'Help me design the architecture for my MVP',
  },
  'Database schema': {
    title: 'Design Database Schema',
    description: 'Create optimized database structures for your application',
    prompt: 'Help me design the database schema for my app',
  },
  'API development': {
    title: 'Develop APIs',
    description: 'Build RESTful or GraphQL APIs for your application',
    prompt: 'Help me develop APIs for my MVP',
  },
  'Frontend UI/UX': {
    title: 'Build Frontend Interface',
    description: 'Create user interfaces that are intuitive and engaging',
    prompt: 'Help me build the frontend for my MVP',
  },
  'Deployment setup': {
    title: 'Set Up Deployment',
    description: 'Configure hosting, CI/CD, and deployment pipelines',
    prompt: 'Help me set up deployment for my application',
  },
  'Tech stack selection': {
    title: 'Choose Tech Stack',
    description: 'Select the right technologies for your project requirements',
    prompt: 'Help me choose the right tech stack for my project',
  },
  'Code generation': {
    title: 'Generate Production Code',
    description: 'Get clean, production-ready code for your features',
    prompt: 'Help me generate code for my feature',
  },

  // Slides capabilities
  'Professional slide design': {
    title: 'Design Professional Slides',
    description: 'Create visually stunning slides with modern design principles',
    prompt: 'Help me create a professional presentation',
  },
  'Animated presentations': {
    title: 'Create Animated Presentations',
    description: 'Add engaging animations and transitions to your slides',
    prompt: 'Help me create an animated presentation',
  },
  'Beautiful typography': {
    title: 'Enhance Typography',
    description: 'Use typography effectively for impact and readability',
    prompt: 'Help me improve the typography in my presentation',
  },
  'Slide transitions & effects': {
    title: 'Add Transitions and Effects',
    description: 'Create smooth transitions between slides for professional flow',
    prompt: 'Help me add professional transitions to my slides',
  },
  'Content structure & flow': {
    title: 'Structure Presentation Content',
    description: 'Organize your message for maximum clarity and impact',
    prompt: 'Help me structure my presentation content',
  },
  'Visual storytelling': {
    title: 'Tell Stories Visually',
    description: 'Transform information into compelling visual narratives',
    prompt: 'Help me tell a visual story in my presentation',
  },
  'Data visualization slides': {
    title: 'Create Data Visualizations',
    description: 'Turn complex data into clear, impactful charts and graphs',
    prompt: 'Help me visualize data in my presentation',
  },
  'Brand-consistent templates': {
    title: 'Create Branded Templates',
    description: 'Design templates that match your brand guidelines',
    prompt: 'Help me create branded presentation templates',
  },
  'Pitch deck creation': {
    title: 'Create a Pitch Deck',
    description: 'Build investor-ready pitch decks that tell your story',
    prompt: 'Help me create a pitch deck for investors',
  },
};
